import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCheck, Trash2, Inbox } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60000;

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, count] = await Promise.all([
        axios.get(`${API}/api/notifications`, { withCredentials: true }),
        axios.get(`${API}/api/notifications/unread-count`, { withCredentials: true }),
      ]);
      setItems(list.data);
      setUnread(count.data.unread || 0);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/notifications/unread-count`, { withCredentials: true });
      setUnread(res.data.unread || 0);
    } catch (err) {
      console.error('Failed to fetch unread count', err);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/api/notifications/${id}/read`, {}, { withCredentials: true });
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (err) {
      console.error('Failed to mark notification read', err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.patch(`${API}/api/notifications/read-all`, {}, { withCredentials: true });
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
      toast.success('All notifications marked as read');
    } catch (err) {
      console.error('Failed to mark all read', err);
      toast.error('Failed to mark all read');
    }
  };

  const deleteOne = async (id) => {
    try {
      await axios.delete(`${API}/api/notifications/${id}`, { withCredentials: true });
      // Use functional update so we always see the latest state (no stale closure)
      setItems((prev) => {
        const wasUnread = prev.find((n) => n.id === id)?.is_read === false;
        if (wasUnread) setUnread((u) => Math.max(0, u - 1));
        return prev.filter((n) => n.id !== id);
      });
    } catch (err) {
      console.error('Failed to delete notification', err);
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all notifications?')) return;
    try {
      await axios.delete(`${API}/api/notifications`, { withCredentials: true });
      setItems([]);
      setUnread(0);
    } catch (e) { toast.error('Failed to clear'); }
  };

  const badgeText = useMemo(() => (unread > 99 ? '99+' : String(unread)), [unread]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full hover:bg-slate-100"
          data-testid="notification-bell-button"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-slate-700" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
              data-testid="notification-unread-badge"
            >
              {badgeText}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" data-testid="notification-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h3>
            <p className="text-xs text-slate-500">{unread} unread</p>
          </div>
          <div className="flex gap-1">
            {items.length > 0 && (
              <>
                <Button size="sm" variant="ghost" onClick={markAllRead} title="Mark all read" data-testid="notification-mark-all-read">
                  <CheckCheck className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={clearAll} title="Clear all" data-testid="notification-clear-all" className="text-red-600 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="notification-list">
          {loading && items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <p>You're all caught up!</p>
            </div>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                className={`px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors ${
                  n.is_read ? '' : 'bg-blue-50/40'
                }`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {!n.is_read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                      <p className="text-sm font-semibold text-slate-900 truncate">{n.title}</p>
                    </div>
                    <p className="text-xs text-slate-600 line-clamp-2">{n.preview}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    {!n.is_read && (
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => markRead(n.id)} title="Mark read" data-testid={`notification-mark-read-${n.id}`}>
                        <Check className="w-3 h-3" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-600" onClick={() => deleteOne(n.id)} title="Delete" data-testid={`notification-delete-${n.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
